import { setActiveSidebarTab } from 'client/actions/actionsManagement'
import ActionsBox from 'client/components/core/BaseEntity/ActionsBox'
import BaseEntityNewUi from 'client/components/core/BaseEntity/ui/BaseEntityNewUi'
import * as utils from 'client/components/core/BaseEntity/utils'
import {
  NO_FOLLOW_CHECKBOX_ID,
  OPEN_IN_NEW_TAB_CHECKBOX_ID,
} from 'client/components/core/tiptap-editor/components/link-toolbar/link-toolbar'
import { color } from 'client/constants/color'
import { useHighlightedEntityId } from 'client/context/HighlightedContext'
import { useSidebarVisibilityActionsContext } from 'client/context/SidebarVisibilityContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { getBubblingPath } from 'client/utils/get-bubbling-path'
import React, { ReactNode, useRef, useState } from 'react'

export type BaseSubentityProps = {
  subentity: {
    id: string
  }
  isEditing: boolean
  flex?: boolean
  flexColumn?: boolean
  title: string
  highlightedColor?: string
  hoverColor?: string
  children?: ReactNode
  isRemoveAllowed?: boolean
  onEdit: () => void
  onRemove: () => void
}

// NOTE: this component is created for subentities
export function BaseSubentity({
  subentity,
  isEditing,
  flex,
  flexColumn,
  title,
  highlightedColor = color.blue,
  hoverColor = color.orange,
  children,
  isRemoveAllowed,
  onEdit,
  onRemove,
}: BaseSubentityProps) {
  const dispatch = useAppDispatch()
  const actionsBoxRef = useRef(null)
  const [showMenu, setShowMenu] = useState(false)
  const highlightedId = useHighlightedEntityId()
  const isHighlighted = highlightedId === subentity.id

  const { showSidebar } = useSidebarVisibilityActionsContext()

  const handleToggleSettings = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation()
    const bubblingPath = getBubblingPath(e)
    if (
      !bubblingPath.some(({ id }) =>
        [NO_FOLLOW_CHECKBOX_ID, OPEN_IN_NEW_TAB_CHECKBOX_ID].includes(id),
      ) &&
      e.target.nodeName !== 'INPUT'
    ) {
      e.preventDefault()
    }

    if (utils.notContainActionButtons(e, actionsBoxRef)) {
      onEdit()
      dispatch(setActiveSidebarTab(null))
      showSidebar()
    }
  }

  const hover = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    if (!isEditing) {
      setShowMenu(true)
    }
  }

  const unHover = (e: React.SyntheticEvent) => {
    e.stopPropagation()

    if (utils.relatedTargetNotContainsActionButtons(e, actionsBoxRef)) {
      setShowMenu(false)
    }
  }

  const getStateColor = () => {
    if (isHighlighted || isEditing) {
      return highlightedColor
    }
    if (showMenu) {
      return hoverColor
    }
    return null
  }

  return (
    <BaseEntityNewUi
      flex={flex}
      flexColumn={flexColumn}
      id={subentity.id}
      stateColor={getStateColor()}
      onClick={handleToggleSettings}
      isEditing={isEditing}
      onMouseOver={hover}
      onMouseOut={unHover}
    >
      {showMenu && (
        <ActionsBox
          ref={actionsBoxRef}
          title={title}
          isRemoveAllowed={isRemoveAllowed}
          toggleSettings={handleToggleSettings}
          remove={onRemove}
          backgroundColor={hoverColor}
        />
      )}
      {children}
    </BaseEntityNewUi>
  )
}
